// frontend/src/services/api/modules/providers.js

import { API_URL } from '../config';
import { getAuthHeaders } from '../utils/fetchWrapper';

/**
 * Invitar a un nuevo proveedor de servicios
 */
export async function invitarProveedor(datos) {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/proveedores`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify(datos)
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Error al invitar proveedor');
    }
    
    return result;
  } catch (error) {
    console.error('Error invitando proveedor:', error);
    return { error: error.message };
  }
}

/**
 * Obtener lista de proveedores por propietario
 */
export async function obtenerProveedores(ownerId) {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/proveedores/porOwner/${ownerId}`, {
      headers: authHeaders
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Error al obtener proveedores');
    }
    
    return result;
  } catch (error) {
    console.error('Error obteniendo proveedores:', error);
    return { error: error.message };
  }
}

export async function obtenerProveedoresConToken(ownerId, token) {
  try {
    const response = await fetch(`${API_URL}/api/proveedores/porOwner/${ownerId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    const result = await response.json();

    // Si el servidor ya devuelve { success: true/false, data: [...] }
    if (result.success !== undefined) {
      return result;
    }
    
    // Si no, adaptamos la respuesta
    if (!response.ok) {
      return { 
        success: false, 
        error: result.error || 'Error al obtener proveedores',
        data: []
      };
    }
    
    // Si la respuesta es exitosa pero no tiene el formato esperado
    return {
      success: true,
      data: result.data || result || []
    };
    
  } catch (error) {
    console.error('[API] Error en catch:', error);
    return { 
      success: false, 
      error: error.message,
      data: []
    };
  }
}

/**
 * Actualizar datos de un proveedor
 */
export async function actualizarProveedor(id, datos) {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/proveedores/${id}`, {
      method: 'PATCH',
      headers: authHeaders,
      body: JSON.stringify(datos)
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Error al actualizar proveedor');
    }
    
    return result;
  } catch (error) {
    console.error('Error actualizando proveedor:', error);
    return { error: error.message };
  }
}

/**
 * Re-invitar a un proveedor (nuevo token)
 */
export async function reInvitarProveedor(id) {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/proveedores/${id}/reinvitar`, {
      method: 'POST',
      headers: authHeaders  
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Error al re-invitar proveedor');
    }
    
    return result;
  } catch (error) {
    console.error('Error re-invitando proveedor:', error);
    return { error: error.message };
  }
}

/**
 * Eliminar/revocar acceso a un proveedor
 */
export async function revocarProveedor(id) {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/proveedores/${id}/revocar`, {
      method: 'DELETE',
      headers: authHeaders
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Error al revocar proveedor');
    }
    
    return result;
  } catch (error) {
    console.error('Error revocando proveedor:', error);
    return { error: error.message };
  }
}

/**
 * Verificar token de proveedor (aceptar/rechazar invitación)
 */
export async function verificarProveedor(token, accion) {
  try {
    const response = await fetch(`${API_URL}/api/proveedores/verificar`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        token, 
        accion  // 'aceptar' o 'rechazar'
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error al verificar proveedor');
    }

    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
}

/**
 * Eliminar proveedor de la lista del usuario
 */
export async function eliminarProveedor(id, token) {
  try {
    const headers = token ? {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    } : await getAuthHeaders();
    
    const response = await fetch(`${API_URL}/api/proveedores/${id}`, {
      method: 'DELETE',
      headers: headers
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Error al eliminar proveedor');
    }
    
    return result;
  } catch (error) {
    console.error('Error eliminando proveedor:', error);
    return { error: error.message };
  }
}

/**
 * Crear recordatorio programado para proveedor
 */
export async function crearRecordatorioProgramado(objetoId, datos) {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/objetos/${objetoId}/recordatorio-programado`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify(datos)
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Error al crear recordatorio programado');
    }
    
    return result;
  } catch (error) {
    console.error('Error creando recordatorio programado:', error);
    return { error: error.message };
  }
}

/**
 * Obtener recordatorios programados de una mascota
 */
export async function obtenerRecordatoriosProgramados(objetoId) {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/objetos/${objetoId}/recordatorios-programados`, {
      headers: authHeaders
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Error al obtener recordatorios programados');
    }
    
    return result;
  } catch (error) {
    console.error('Error obteniendo recordatorios programados:', error);
    return { error: error.message };
  }
}

/**
 * Actualizar recordatorio programado
 */
export async function actualizarRecordatorioProgramado(id, datos) {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/recordatorios-programados/${id}`, {
      method: 'PATCH',
      headers: authHeaders,
      body: JSON.stringify(datos)
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Error al actualizar recordatorio');
    }
    
    return result;
  } catch (error) {
    console.error('Error actualizando recordatorio programado:', error);
    return { error: error.message };
  }
}

/**
 * Eliminar recordatorio programado
 */
export async function eliminarRecordatorioProgramado(id) {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/recordatorios-programados/${id}`, {
      method: 'DELETE',
      headers: authHeaders
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Error al eliminar recordatorio');
    }
    
    return result;
  } catch (error) {
    console.error('Error eliminando recordatorio programado:', error);
    return { error: error.message };
  }
}